import fs from 'node:fs';
import os from 'node:os';
import path from 'node:path';
import {
  copySkill,
  readSkillFile,
  type SkillName,
} from '@standardagents/skill';
import { logger } from '../utils/logger.js';

export type InstallTargetId = 'codex' | 'claude';

export interface SkillCommandOptions {
  agent?: InstallTargetId;
  dir?: string;
  force?: boolean;
}

export interface InstallTarget {
  id: InstallTargetId;
  label: string;
  summary: string;
  detected: boolean;
  installPath: string;
}

const DEFAULT_SKILL_NAME: SkillName = 'agentbuilder';

export function resolveCodexHome(
  env: NodeJS.ProcessEnv = process.env,
  homeDir = os.homedir()
): string {
  return env.CODEX_HOME || path.join(homeDir, '.codex');
}

export function resolveClaudeHome(homeDir = os.homedir()): string {
  return path.join(homeDir, '.claude');
}

export function expandHomePath(inputPath: string, homeDir = os.homedir()): string {
  if (inputPath === '~') {
    return homeDir;
  }

  if (inputPath.startsWith('~/')) {
    return path.join(homeDir, inputPath.slice(2));
  }

  return inputPath;
}

export function buildInstallTargets(
  options: SkillCommandOptions = {},
  env: NodeJS.ProcessEnv = process.env,
  homeDir = os.homedir()
): InstallTarget[] {
  const customRoot = options.dir
    ? path.resolve(expandHomePath(options.dir, homeDir))
    : null;

  const codexHome = resolveCodexHome(env, homeDir);
  const claudeHome = resolveClaudeHome(homeDir);

  return [
    {
      id: 'codex',
      label: 'Codex',
      summary: 'Install as a Codex skill folder',
      detected: fs.existsSync(codexHome),
      installPath: customRoot ?? path.join(codexHome, 'skills'),
    },
    {
      id: 'claude',
      label: 'Claude Code',
      summary: 'Install as a Claude Code user subagent',
      detected: fs.existsSync(claudeHome),
      installPath: customRoot ?? path.join(claudeHome, 'agents'),
    },
  ];
}

export function resolveSelectedTarget(
  targetId: InstallTargetId,
  options: SkillCommandOptions = {},
  env: NodeJS.ProcessEnv = process.env,
  homeDir = os.homedir()
): InstallTarget {
  const target = buildInstallTargets(options, env, homeDir).find(
    (candidate) => candidate.id === targetId
  );

  if (!target) {
    throw new Error(`Unknown install target "${targetId}".`);
  }

  return target;
}

export function buildClaudeSubagentMarkdown(
  skillName: SkillName,
  description: string,
  body: string
): string {
  const normalizedBody = body.replace(/^\uFEFF/, '').trim();

  return [
    '---',
    `name: ${skillName}`,
    `description: ${description}`,
    '---',
    '',
    normalizedBody,
    '',
  ].join('\n');
}

async function selectInstallTarget(targets: InstallTarget[]): Promise<InstallTarget> {
  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;
    let selectedIndex = 0;

    const render = (initial = false) => {
      const lines: string[] = [];
      lines.push('Choose where to install the AgentBuilder skill:');
      lines.push('');

      for (let i = 0; i < targets.length; i++) {
        const target = targets[i];
        const isSelected = i === selectedIndex;
        const cursor = isSelected ? '\x1B[36m❯\x1B[0m' : ' ';
        const title = isSelected ? '\x1B[36m' : '\x1B[90m';
        const detection = target.detected ? '\x1B[32m(detected)\x1B[0m' : '\x1B[90m(not found yet)\x1B[0m';

        lines.push(`${cursor} ${title}${target.label}\x1B[0m ${detection}`);
        lines.push(`  ${target.summary}`);
        lines.push(`  ${target.installPath}`);
        lines.push('');
      }

      lines.push('Use arrows to move, Enter to install, Ctrl+C to cancel.');

      if (!initial) {
        stdout.write('\x1B[?25l');
        stdout.write(`\x1B[${lines.length}A`);
        stdout.write('\x1B[0J');
      }

      stdout.write(lines.join('\n') + '\n');
    };

    render(true);

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    const cleanup = () => {
      stdin.setRawMode(wasRaw ?? false);
      stdin.pause();
      stdin.removeListener('data', onData);
      stdout.write('\x1B[?25h');
    };

    const onData = (key: string) => {
      if (key === '\x1B[A' || key === 'k') {
        selectedIndex = (selectedIndex - 1 + targets.length) % targets.length;
        render();
      } else if (key === '\x1B[B' || key === 'j') {
        selectedIndex = (selectedIndex + 1) % targets.length;
        render();
      } else if (key === '\r' || key === '\n') {
        cleanup();
        resolve(targets[selectedIndex]);
      } else if (key === '\x03') {
        cleanup();
        stdout.write('\n');
        process.exit(1);
      }
    };

    stdin.on('data', onData);
  });
}

export async function installBundledSkill(
  targetId: InstallTargetId,
  skillName: SkillName = DEFAULT_SKILL_NAME,
  options: SkillCommandOptions = {}
): Promise<string> {
  const target = resolveSelectedTarget(targetId, options);

  if (target.id === 'codex') {
    const targetDir = path.join(target.installPath, skillName);

    if (fs.existsSync(targetDir) && !options.force) {
      throw new Error(
        `Skill already exists at ${targetDir}. Re-run with --force to overwrite it.`
      );
    }

    return copySkill(skillName, target.installPath, {
      overwrite: options.force ?? false,
    });
  }

  const targetFile = path.join(target.installPath, `${skillName}.md`);

  if (fs.existsSync(targetFile) && !options.force) {
    throw new Error(
      `Claude subagent already exists at ${targetFile}. Re-run with --force to overwrite it.`
    );
  }

  const skillBody = await readSkillFile(skillName);
  const subagentMarkdown = buildClaudeSubagentMarkdown(
    skillName,
    'Use for Standard Agents and AgentBuilder architecture, composition, subagent design, and implementation work.',
    skillBody
  );

  fs.mkdirSync(target.installPath, { recursive: true });
  fs.writeFileSync(targetFile, subagentMarkdown, 'utf8');

  return targetFile;
}

function isInteractiveTerminal(): boolean {
  return Boolean(process.stdin.isTTY && process.stdout.isTTY);
}

export async function skill(options: SkillCommandOptions = {}): Promise<void> {
  try {
    const selectedTarget = options.agent
      ? resolveSelectedTarget(options.agent, options)
      : isInteractiveTerminal()
        ? await selectInstallTarget(buildInstallTargets(options))
        : resolveSelectedTarget('codex', options);

    logger.log('');
    logger.info(`Installing AgentBuilder skill into ${selectedTarget.label}...`);

    const installedPath = await installBundledSkill(
      selectedTarget.id,
      DEFAULT_SKILL_NAME,
      options
    );

    logger.success(`Installed ${DEFAULT_SKILL_NAME} into ${selectedTarget.label}`);
    logger.log('');
    logger.log(`Path: ${installedPath}`);
    logger.log('');
    logger.log('Next steps:');

    if (selectedTarget.id === 'codex') {
      logger.log('  Restart Codex if it is already running');
      logger.log(`  Edit ${path.join(installedPath, 'SKILL.md')} to continue customizing the skill`);
    } else {
      logger.log('  Restart Claude Code if it is already running');
      logger.log(`  Edit ${installedPath} to continue customizing the subagent`);
    }
  } catch (error) {
    logger.log('');
    logger.error(error instanceof Error ? error.message : 'Failed to install skill');
    process.exit(1);
  }
}
